import { useEffect, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import { componentDisplayName, enabledOfKind } from '../../../domain/Component';
import type { IrrigationScheduleInput } from '../../ports/IrrigationRepository';

// La API/DB guardan la duración en segundos (más preciso para el scheduler y
// el cálculo de litros), pero minutos es lo que tiene sentido mostrar y
// tipear en el formulario — la conversión vive en los bordes de este hook
// (al cargar y al guardar), nunca en la UI.
export interface IrrigationScheduleForm {
  enabled: boolean;
  daysOfWeek: number[];
  startTime: string;
  durationMinutes: number;
  flowRateLpm: number;
  rainSkipEnabled: boolean;
  rainSkipProbabilityPct: number;
  humidityDeviceId: string | null;
  humidityChannel: number | null;
  humiditySkipThresholdPct: number | null;
  notifyOnSkip: boolean;
}

// Una sonda de suelo de cualquier estación del usuario, para vincular.
export interface SoilProbeOption {
  deviceId: string;
  channel: number;
  label: string;
}

const emptyForm: IrrigationScheduleForm = {
  enabled: true,
  daysOfWeek: [],
  startTime: '06:30',
  durationMinutes: 10,
  flowRateLpm: 5,
  rainSkipEnabled: true,
  rainSkipProbabilityPct: 60,
  humidityDeviceId: null,
  humidityChannel: null,
  humiditySkipThresholdPct: null,
  notifyOnSkip: true,
};

export function useIrrigationScheduleForm(deviceId: string, channel: number) {
  const { devices: deviceRepo, irrigation } = useRepositories();

  const [device, setDevice] = useState<Device | null>(null);
  const [soilProbes, setSoilProbes] = useState<SoilProbeOption[]>([]);
  const [form, setForm] = useState<IrrigationScheduleForm>(emptyForm);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Formulario de edición: se carga una vez al entrar a la pantalla (no hace
  // falta refrescar cada vez que se vuelve de otro tab, a diferencia de
  // useIrrigationControl que sí necesita useIonViewWillEnter para reflejar
  // un riego que se prendió/apagó mientras el usuario estaba en otra vista).
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [{ device: found }, { devices: all }, { schedule }] = await Promise.all([
          deviceRepo.get(deviceId),
          deviceRepo.list(),
          irrigation.getSchedule(deviceId, channel),
        ]);
        setDevice(found);
        setSoilProbes(
          all.flatMap((d) =>
            enabledOfKind(d.components, 'soil').map((c) => ({
              deviceId: d.id,
              channel: c.channel,
              label: d.id === deviceId ? componentDisplayName(c) : `${d.name} · ${componentDisplayName(c)}`,
            }))
          )
        );
        if (schedule) {
          setForm({
            enabled: schedule.enabled,
            daysOfWeek: schedule.daysOfWeek,
            startTime: schedule.startTime,
            durationMinutes: Math.round(schedule.durationSeconds / 60),
            flowRateLpm: schedule.flowRateLpm,
            rainSkipEnabled: schedule.rainSkipEnabled,
            rainSkipProbabilityPct: schedule.rainSkipProbabilityPct,
            humidityDeviceId: schedule.humidityDeviceId,
            humidityChannel: schedule.humidityChannel,
            humiditySkipThresholdPct: schedule.humiditySkipThresholdPct,
            notifyOnSkip: schedule.notifyOnSkip,
          });
          setHasSchedule(true);
        } else {
          setForm(emptyForm);
          setHasSchedule(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo cargar el horario de riego.');
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceId, channel, deviceRepo, irrigation]);

  const updateForm = (patch: Partial<IrrigationScheduleForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setSaved(false);
  };

  const toggleDay = (day: number) => {
    updateForm({
      daysOfWeek: form.daysOfWeek.includes(day)
        ? form.daysOfWeek.filter((d) => d !== day)
        : [...form.daysOfWeek, day].sort(),
    });
  };

  // Devuelve si guardó con éxito — la pantalla lo usa para decidir si navega
  // a otro lado después de guardar (el estado `error`/`saved` del hook
  // todavía no se actualizó en el closure de quien llama en el mismo tick).
  const handleSave = async (): Promise<boolean> => {
    if (form.daysOfWeek.length === 0) {
      setError('Elegí al menos un día de la semana.');
      return false;
    }
    if (form.durationMinutes <= 0) {
      setError('La duración debe ser mayor a 0 minutos.');
      return false;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const { durationMinutes, ...rest } = form;
      const input: IrrigationScheduleInput = {
        ...rest,
        durationSeconds: Math.round(durationMinutes * 60),
        humidityChannel: form.humidityDeviceId ? (form.humidityChannel ?? 1) : null,
        humiditySkipThresholdPct: form.humidityDeviceId ? (form.humiditySkipThresholdPct ?? 50) : null,
      };
      await irrigation.saveSchedule(deviceId, channel, input);
      setHasSchedule(true);
      setSaved(true);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el horario.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      await irrigation.deleteSchedule(deviceId, channel);
      setForm(emptyForm);
      setHasSchedule(false);
      setSaved(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar el horario.');
    } finally {
      setSaving(false);
    }
  };

  const valveComponent = device?.components.find((c) => c.kind === 'valve' && c.channel === channel);
  const valveName = valveComponent ? componentDisplayName(valveComponent) : `Sector ${channel}`;

  return {
    device,
    soilProbes,
    valveName,
    form,
    hasSchedule,
    loading,
    saving,
    error,
    saved,
    updateForm,
    toggleDay,
    handleSave,
    handleDelete,
  };
}
